'use client'

import { TrustpilotCarousel } from 'components/TrustpilotCarousel'
import { useMemoizedContentGetter, useInlineMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import SectionContainer from 'components/Section/SectionContainer'
import { useEffect, useRef, useState, useMemo, useCallback, memo } from 'react'
import useSearchParamChecker from 'utils/queryManipulation/useSearchParamChecker'
import { usePathname } from 'next/navigation'

// Custom Trustpilot Review Card - Memoized to prevent unnecessary re-renders
const TrustpilotReviewCard = memo(function TrustpilotReviewCard({
  rating = 5,
  verified = true,
  title,
  content,
  author,
  date,
}) {
  // Pre-calculate stars array to avoid hydration issues
  const stars = [rating >= 1, rating >= 2, rating >= 3, rating >= 4, rating >= 5]

  return (
    <div
      className="bg-white rounded p-6 flex flex-col gap-3 w-full h-full min-h-[191px]"
      style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}
    >
      {/* Stars and Verified */}
      <div className="flex items-center gap-2">
        <div className="flex gap-0.5">
          {stars.map((filled, i) => (
            <div
              key={i}
              className="flex items-center justify-center"
              style={{
                width: '21.451px',
                height: '21.975px',
                backgroundColor: filled ? '#51B37F' : '#E5E7EB',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="white">
                <path d="M10 0l2.5 6.5H19l-5.5 4.5 2 6.5L10 13l-5.5 4.5 2-6.5L1 6.5h6.5z" />
              </svg>
            </div>
          ))}
        </div>
        {verified && (
          <div className="flex items-center gap-1">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#51B37F">
              <circle cx="8" cy="8" r="8" />
              <path d="M6 8l2 2 4-4" stroke="white" strokeWidth="2" fill="none" />
            </svg>
            <span className="text-xs text-gray-600">Verified</span>
          </div>
        )}
      </div>

      {/* Title */}
      {title ? <h3 className="font-bold text-base text-gray-900">{title}</h3> : null}

      {/* Content */}
      {content ? (
        <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">{content}</p>
      ) : null}

      {/* Author and Date */}
      <div className="text-xs text-gray-500 mt-auto">
        <span className="font-semibold">{author || 'Anonymous'}</span>
        {date ? <span>, {date}</span> : null}
      </div>
    </div>
  )
})

const TrustPilotStateWidget = ({
  content,
  stateName: stateNameProp,
  stateSlug: stateSlugProp,
  jsonConfig,
}) => {
  // All hooks must be called at the top, before any early returns
  const isDisabledParamPresent = useSearchParamChecker('disableFeatures', 'trustpilot_state_widget')
  const ref = useRef(null)
  const scrollContainerRef = useRef(null)
  const pathname = usePathname()
  const [currentSlide, setCurrentSlide] = useState(0)

  // Always call hooks unconditionally
  const cmsContent = useMemoizedContentGetter(content, [
    'config',
    'eyebrow',
    'subtitle',
    'title',
    'backgroundConfig',
  ])

  const cmsJsonContent = useInlineMemoizedContentGetter(content?.fields?.additionalConfiguration, [
    'jsonContent',
  ])

  // Memoize computed values to prevent re-renders
  const customReviews = useMemo(() => jsonConfig?.reviews || null, [jsonConfig?.reviews])
  const ratingText = useMemo(() => jsonConfig?.ratingText || null, [jsonConfig?.ratingText])

  const stateSlug = useMemo(
    () =>
      stateSlugProp ||
      String(pathname || '')
        .split('?')[0]
        .split('/')
        .filter(Boolean)
        .pop(),
    [stateSlugProp, pathname]
  )

  const stateName = useMemo(
    () =>
      stateNameProp ||
      String(stateSlug || '')
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    [stateNameProp, stateSlug]
  )

  // Original Trustpilot useEffect
  useEffect(() => {
    if (window.Trustpilot && !isDisabledParamPresent) {
      window.Trustpilot.loadFromElement(ref.current, true)
    }
  }, [isDisabledParamPresent])

  // Handle scroll to update current slide indicator - memoize the handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || !customReviews || customReviews.length === 0) return

    const scrollLeft = container.scrollLeft
    const containerWidth = container.offsetWidth
    const gap = 16 // 1rem
    const cardWidth = containerWidth - 32 // 100% - 2rem (padding)
    const totalWidth = cardWidth + gap
    const newSlide = Math.round(scrollLeft / totalWidth)
    setCurrentSlide(Math.min(Math.max(0, newSlide), customReviews.length - 1))
  }, [customReviews])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !customReviews || customReviews.length === 0) return

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [customReviews, handleScroll])

  // If jsonConfig is provided, use it directly; otherwise use content from CMS
  const { config, title, backgroundConfig } = useMemo(
    () =>
      jsonConfig
        ? {
            config: jsonConfig.config,
            title: jsonConfig.title,
            backgroundConfig: jsonConfig.backgroundConfig,
          }
        : cmsContent,
    [jsonConfig, cmsContent]
  )

  const { jsonContent } = useMemo(
    () =>
      jsonConfig?.additionalConfiguration
        ? { jsonContent: jsonConfig.additionalConfiguration }
        : cmsJsonContent,
    [jsonConfig?.additionalConfiguration, cmsJsonContent]
  )

  const trustpilotConfig = useMemo(() => config?.jsonContent || config || {}, [config])

  const mergedConfig = useMemo(
    () => ({
      ...trustpilotConfig,
      tags: trustpilotConfig?.tags || stateSlug || '',
      className: trustpilotConfig?.className
        ? `${trustpilotConfig.className} w-full`
        : 'trustpilot-widget w-full',
    }),
    [trustpilotConfig, stateSlug]
  )

  const resolvedTitle = useMemo(
    () => title || (stateName ? `Reviews and Testimonials from ${stateName}s` : null),
    [title, stateName]
  )

  // Memoize scroll to slide function
  const scrollToSlide = useCallback((index) => {
    const container = scrollContainerRef.current
    if (container) {
      const containerWidth = container.offsetWidth
      const gap = 16 // 1rem
      const cardWidth = containerWidth - 32 // 100% - 2rem (padding)
      const totalWidth = cardWidth + gap
      container.scrollTo({
        left: index * totalWidth,
        behavior: 'smooth',
      })
    }
  }, [])

  if (isDisabledParamPresent || isDisabledParamPresent == null) return null

  return (
    <Section
      data-testid="trustpilot-state-widget"
      variant={mergedConfig?.backgroundColor ?? null}
      contain={false}
      defaultPadY
      sectionStyles={jsonContent?.style ?? null}
      fullBackground={backgroundConfig?.jsonContent?.fullBackground ?? false}
    >
      <SectionContainer>
        {resolvedTitle && (
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-left">
            {resolvedTitle}
          </h2>
        )}

        {customReviews && customReviews.length > 0 ? (
          <>
            {/* Mobile: Horizontal Scroll Carousel */}
            <div className="md:hidden">
              <div className="overflow-hidden -mx-4">
                <div
                  ref={scrollContainerRef}
                  className="overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                  style={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridAutoColumns: 'calc(100% - 2rem)',
                    gap: '1rem',
                    paddingLeft: '1rem',
                    paddingRight: '1rem',
                    paddingBottom: '1.5rem',
                  }}
                >
                  {customReviews.map((review, index) => (
                    <div key={index} className="snap-center">
                      <TrustpilotReviewCard
                        rating={review.rating || 5}
                        verified={review.verified !== false}
                        title={review.title}
                        content={review.content}
                        author={review.author}
                        date={review.date}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Carousel Indicators */}
              {customReviews.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {customReviews.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => scrollToSlide(index)}
                      className="w-2 h-2 rounded-full transition-colors"
                      style={{
                        backgroundColor: currentSlide === index ? '#3B82F6' : '#D1D5DB',
                      }}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Rating Text */}
              {ratingText && <p className="text-sm text-gray-600 text-center mt-4">{ratingText}</p>}
            </div>

            {/* Desktop: Grid Layout */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {customReviews.map((review, index) => (
                <div key={index} className="w-[279px]">
                  <TrustpilotReviewCard
                    rating={review.rating || 5}
                    verified={review.verified !== false}
                    title={review.title}
                    content={review.content}
                    author={review.author}
                    date={review.date}
                  />
                </div>
              ))}
            </div>

            {/* Rating Text for Desktop */}
            {ratingText && (
              <p className="hidden md:block text-sm text-gray-600 text-center">{ratingText}</p>
            )}
          </>
        ) : (
          <TrustpilotCarousel reference={ref} {...mergedConfig} />
        )}
      </SectionContainer>
    </Section>
  )
}

export { TrustPilotStateWidget }
export default TrustPilotStateWidget
